import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useErrorNotice } from '../../../hooks/useErrorNotice';
import { makeStyles } from '@material-ui/core/styles';
import ee from '../../../services/earth-engine';
import {
  Box,
  Button,
  FormLabel,
  Radio,
  Grid,
  Card,
  CardContent,
  Typography,
  CardActions,
  IconButton,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@material-ui/core';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import Fullscreen from '@material-ui/icons/Fullscreen';
import DoneIcon from '@material-ui/icons/Done';
import StepperButtons from '../acquisition/StepperButtons';
import { FINALIZE } from '../../../hooks/useStepNavigations';
import { Actions as EUSCoast } from '../../../store/ducks/euscoast';
import {
  trainAndClassify,
  getMapBiomasClassificationYear,
  getMapBiomasClassificationsList,
} from '../../../algorithms/euscoast';
import { buildPeriodIndex, mapBiomasYear } from '../../../algorithms/utils';
import { useLocalStorage } from '../../../common/utils';
import TourGuider from '../tour/TourGuider';
import HelpButton from '../core/HelpButton';
import { TerraDrawManager } from '../../../common/TerraDrawManager';
import { OVERLAY_PANEL_GAP, OVERLAY_TOP_BAR } from '../../pages/Shoreline/theme';
import { OVERLAY_COLUMN_BOTTOM_SPACE } from '../../../theme';
import { aoiPaths } from '../../../common/aoi';

/**
 * @constant LANDCOVER_COLUMN_TOP_INSET
 * @description Distance, in px, from the top of the step's map area down to the first card of
 * the overlay column (the column's own `margin-top`).
 * @memberof module:EUSCoast
 */
const LANDCOVER_COLUMN_TOP_INSET = 10;

/**
 * @constant LANDCOVER_COLUMN_BOTTOM_RESERVE
 * @description Height, in px, to subtract from the viewport when capping the overlay column of
 * this step, so its end stays on screen and its last cards can actually be scrolled to.
 * The column used to be capped at `97vh`, which does not hold here: the map area it sits in is
 * `height: 100vh` but starts *below* the navigation bar, so its bottom already falls off screen
 * and `overflow: hidden` clips it - the last cards (the land-cover list and the "generate
 * raster" button) ended up in the clipped strip, unreachable however far the column scrolled.
 * Accounts for the bar, the column's top inset and the breathing room left under the last card,
 * matching how the result pages cap their own panel columns.
 * @see {@link OVERLAY_TOP_BAR}
 * @see {@link OVERLAY_COLUMN_BOTTOM_SPACE}
 * @memberof module:EUSCoast
 */
const LANDCOVER_COLUMN_BOTTOM_RESERVE = OVERLAY_TOP_BAR + LANDCOVER_COLUMN_TOP_INSET + OVERLAY_COLUMN_BOTTOM_SPACE;

/**
 * @function overlayToRingCoordinates
 * @description Extracts a closed [lng, lat] ring from a drawn training shape — the four corners
 * of a Rectangle's bounds, or the vertices of a Polygon's path — so both shapes converge on the
 * same GeoJSON ring format for classification/export.
 * @param {google.maps.Rectangle|google.maps.Polygon} overlay
 * **Called by:
 * @see {generateClassificationLayer}
 * @see {exportShapesToCsv}
 * @memberof module:Euscoast
 * @returns {Array<[number, number]>} Closed ring (first point repeated at the end).
 */
const overlayToRingCoordinates = (overlay) => {
  if (typeof overlay.getBounds === 'function') {
    const bounds = overlay.getBounds();
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    return [
      [sw.lng(), sw.lat()],
      [ne.lng(), sw.lat()],
      [ne.lng(), ne.lat()],
      [sw.lng(), ne.lat()],
      [sw.lng(), sw.lat()],
    ];
  }
  const path = overlay
    .getPath()
    .getArray()
    .map((p) => [p.lng(), p.lat()]);
  return [...path, path[0]];
};

const LANDCOVER_SHAPES_STORAGE_KEY = 'euscoastLandCoverShapes';

/**
 * Land-cover value pre-selected when the step opens. EUSCoast analyses urban growth, so drawing
 * starts on human intervention (the urban area) instead of CSqueeze's mangrove.
 */
const DEFAULT_LAND_COVER = 3;

export const mapbiomas_lulc_class_color = `
  <RasterSymbolizer>
  <ChannelSelection>
  <GrayChannel>
  <SourceChannelName>1</SourceChannelName>
  </GrayChannel>
  </ChannelSelection>
  <ColorMap type="values">
    <ColorMapEntry color="#129912" quantity="1" />
    <ColorMapEntry color="#129912" quantity="3" />
    <ColorMapEntry color="#129912" quantity="4" />
    <ColorMapEntry color="#687537" quantity="5" />
    <ColorMapEntry color="#129912" quantity="49" />
    <ColorMapEntry color="#129912" quantity="10" />
    <ColorMapEntry color="#129912" quantity="11" />
    <ColorMapEntry color="#129912" quantity="12" />
    <ColorMapEntry color="#129912" quantity="32" />
    <ColorMapEntry color="#129912" quantity="29" />
    <ColorMapEntry color="#129912" quantity="50" />
    <ColorMapEntry color="#129912" quantity="13" />
    <ColorMapEntry color="#af2a2a" quantity="14" />
    <ColorMapEntry color="#af2a2a" quantity="15" />
    <ColorMapEntry color="#af2a2a" quantity="18" />
    <ColorMapEntry color="#af2a2a" quantity="19" />
    <ColorMapEntry color="#af2a2a" quantity="39" />
    <ColorMapEntry color="#af2a2a" quantity="20" />
    <ColorMapEntry color="#af2a2a" quantity="40" />
    <ColorMapEntry color="#af2a2a" quantity="62" />
    <ColorMapEntry color="#af2a2a" quantity="41" />
    <ColorMapEntry color="#af2a2a" quantity="36" />
    <ColorMapEntry color="#af2a2a" quantity="46" />
    <ColorMapEntry color="#af2a2a" quantity="47" />
    <ColorMapEntry color="#af2a2a" quantity="48" />
    <ColorMapEntry color="#af2a2a" quantity="9" />
    <ColorMapEntry color="#af2a2a" quantity="21" />
    <ColorMapEntry color="#af2a2a" quantity="22" />
    <ColorMapEntry color="#e3c565" quantity="23" />
    <ColorMapEntry color="#af2a2a" quantity="24" />
    <ColorMapEntry color="#af2a2a" quantity="30" />
    <ColorMapEntry color="#af2a2a" quantity="75" />
    <ColorMapEntry color="#af2a2a" quantity="25" />
    <ColorMapEntry color="#0000ff" quantity="26" />
    <ColorMapEntry color="#0000ff" quantity="33" />
    <ColorMapEntry color="#af2a2a" quantity="31" />
    <ColorMapEntry color="#af2a2a" quantity="27" />
  </ColorMap>
  </RasterSymbolizer>`;

const useStyles = makeStyles((theme) => ({
  wrapper: {
    maxWidth: '100vw',
    flexGrow: 1,
    position: 'relative',
  },
  mapContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    flexFlow: 'column',
  },
  mapOverlay: {
    position: 'absolute',
    zIndex: 10,
    pointerEvents: 'none',
    '& > *': {
      pointerEvents: 'none',
      '& > *': {
        pointerEvents: 'auto',
      },
    },
  },
  margin: {
    margin: '5px',
  },
  right: {
    textAlign: 'right',
  },
  landCoverMarkButton: {
    backgroundColor: 'white',
  },
}));

/**
 * @function LandCover
 * @description Fifth step of the EUSCoast acquisition wizard. For each of the two selected
 * years, lets the user manually draw land-cover training polygons (or import MapBiomas/CSV
 * training data), then generate a classified land-cover raster — either via a random forest
 * classifier trained on the drawn polygons, or directly from MapBiomas.
 * @param {Object} props - Component properties.
 * @param {Function} props.navigate - Step navigation handler, forwarded to StepperButtons.
 * @see {@link generateClassificationLayer} - runs the classification for the active year.
 * @see {@link handleDrawing} - registers a drawn training polygon.
 * @see {@link handleFinish} - commits both years' classifications and layers.
 * **Called by:
 * @see {CoastalSqueezePage} - as the fifth acquisition step.
 * @memberof module:Euscoast
 * @returns {JSX.Element} The land-cover classification UI.
 */
const LandCover = ({ navigate }) => {
  const [isTourOpen, setIsTourOpen] = useLocalStorage('showPeriodTour', true);
  const dispatch = useDispatch();
  const classes = useStyles();
  const [t] = useTranslation();
  /**
   * Problems are reported in the corner instead of in a blocking dialog - see {@link useErrorNotice}.
   */
  const notifyError = useErrorNotice();
  const images = useSelector((state) => state.imagery.images);
  const coordinates = useSelector((state) => state.acquisition.coordinates);
  const imageLists = useSelector((state) => state.euscoast.imageList);
  const periodLabels = useSelector((state) => state.euscoast.periodLabels);
  const mapDiv = useRef();
  const mapRef = useRef();
  const drawingManager = useRef();
  const landCoverRef = useRef(null);
  const tempTab = useRef(0);
  const AOI = useRef();
  const bounds = useRef();
  const selectedLandCoverRef = useRef(DEFAULT_LAND_COVER);
  const markedPoints = useRef([]);
  const mapModeRef = useRef(DEFAULT_LAND_COVER);
  const drawShapeRef = useRef('rectangle');
  const medianImagesRequested = useRef(false);
  const [markedPointsCount, setMarkedPointsCount] = useState(1);
  const [selectedLandCover, setSelectedLandCover] = useState(DEFAULT_LAND_COVER);
  const [imagesLayers, setImagesLayers] = useState([]);
  const [layers, setLayers] = useState({});
  const [finish, setFinish] = useState([false, false]);
  const [mapMode, setMapMode] = useState(DEFAULT_LAND_COVER);
  const [years, setYears] = useState([[], []]);
  const [tabKey, setTabKey] = useState(0);
  const [datasets, setDatasets] = useState([]);

  /**
   * @function hasMapBiomasFor
   * @description Whether MapBiomas publishes a classification for the year a tab is showing.
   * The list comes from the collection itself, so it answers for whatever years are actually
   * published - the series ends at the last one MapBiomas has released, which is why recent
   * imagery has no dataset to offer and the card stays hidden.
   * Both sides are reduced to a year before they are compared. A tab is keyed by period rather
   * than by year once a year holds more than one composite - `2018-1` - and the entries name a
   * year in their own way: a plain `2018` in the collection this module reads, a
   * `classification_2018` band id in the other one. Comparing the raw strings answered no to
   * every period tab, and containment would let a partial key match several years.
   * @param {String|Number} key - The tab's key.
   * @see {@link mapBiomasYear}
   * @memberof module:Euscoast
   * @returns {Boolean} True when that year is published.
   */
  const hasMapBiomasFor = (key) => {
    const year = mapBiomasYear(key);
    return year !== null && datasets.some((dataset) => mapBiomasYear(dataset) === year);
  };
  const [falseColor, setFalseColor] = useState(false);
  const [mapBiomasDialogOpen, setMapBiomasDialogOpen] = useState(false);
  const [polygonsFileProcessedDialogOpen, setPolygonsFileProcessedDialogOpen] = useState(false);
  const [mapBiomasConfirmAction, setMapBiomasConfirmAction] = useState(() => () => {});

  useEffect(() => {
    mapModeRef.current = mapMode;
  }, [mapMode]);

  const landCovers = [
    {
      value: 0,
      label: t('forms.euscoast.landcovers.mangrove'),
      class: t('forms.euscoast.landcovers.mangrove'),
      color: '#687537',
    },
    {
      value: 1,
      label: t('forms.euscoast.landcovers.vegetation'),
      class: t('forms.euscoast.landcovers.vegetation'),
      color: '#129912',
    },
    {
      value: 2,
      label: t('forms.euscoast.landcovers.water'),
      class: t('forms.euscoast.landcovers.water'),
      color: '#0000ff',
    },
    {
      value: 3,
      label: t('forms.euscoast.landcovers.human'),
      class: t('forms.euscoast.landcovers.human'),
      color: '#af2a2a',
    },
    {
      value: 4,
      label: t('forms.euscoast.landcovers.beach'),
      class: t('forms.euscoast.landcovers.beach'),
      color: '#e3c565',
    },
    {
      value: 5,
      label: t('forms.euscoast.5.removePolygon'),
      class: t('forms.euscoast.5.removePolygon'),
      color: '#000000',
    },
  ];

  const pointerColors = landCovers.map((landCover) => landCover.color);

  const landCoversDrawOrder = [3, 1, 2, 0, 4, 5].map((value) =>
    landCovers.find((landCover) => landCover.value === value),
  );

  useEffect(() => {
    const datasetList = getMapBiomasClassificationsList();
    setDatasets(datasetList);
    mapRef.current = new window.google.maps.Map(mapDiv.current, {
      center: { lat: -26.285757, lng: -48.73506 },
      zoom: 13,
      scaleControl: true,
      streetViewControl: false,
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        position: window.google.maps.ControlPosition.TOP_CENTER,
      },
      styles: [
        {
          featureType: 'poi',
          stylers: [{ visibility: 'off' }],
        },
      ],
      fullscreenControl: false,
    });
    const formatedCoordinates = aoiPaths(coordinates);
    AOI.current = new window.google.maps.Polygon({
      paths: formatedCoordinates,
      geodesic: true,
      fillColor: 'transparent',
      strokeColor: '#00A391',
      strokeOpacity: 0.6,
      strokeWeight: 5,
      clicable: false,
    });
    AOI.current.setMap(mapRef.current);
    bounds.current = new window.google.maps.LatLngBounds();
    AOI.current
      .getPaths()
      .getArray()
      .flatMap((path) => path.getArray())
      .forEach((coord) => {
        let latLng = new window.google.maps.LatLng(coord.lat(), coord.lng());
        bounds.current.extend(latLng);
      });
    const center = bounds.current.getCenter();
    mapRef.current.setCenter(center);
    mapRef.current.panBy(1000, 0);
    setMapMode(DEFAULT_LAND_COVER);
  }, []);

  useEffect(() => {
    const values = Object.values(images);
    if (values.length > 0) {
      values.forEach((image, i) => {
        (() => {
          const imgOverlay = new Image();
          imgOverlay.src = image.url;
          const imgFalseColor = new Image();
          imgFalseColor.src = image.falseColorUrl;
        })();
        var overlayFalseColor = new window.google.maps.GroundOverlay(image.falseColorUrl, bounds.current);
        var overlay = new window.google.maps.GroundOverlay(image.url, bounds.current);
        overlayFalseColor.setMap(mapRef.current);
        overlay.setMap(mapRef.current);
        overlayFalseColor.setMap(null);
        if (i !== 0) overlay.setMap(null);

        setImagesLayers((previous) => ({
          ...previous,
          [image.date]: { ...image, overlay, overlayFalseColor },
        }));

        setLayers((previous) => ({
          ...previous,
          [image.date]: { ...image, overlay, overlayFalseColor },
        }));
        markedPoints.current = { ...markedPoints.current, [image.date]: [] };
        restoreSavedShapes(image.date);
      });
      setTabKey(values[0].date ?? 0);
      window.layers = layers;
      window.imagesLayers = imagesLayers;
    }
  }, [images]);

  useEffect(() => {
    if (years.map((y) => y[0]).indexOf(tabKey) === -1) return;
    changeTab(tabKey);
    tempTab.current = tabKey;
  }, [tabKey, falseColor]);

  useEffect(() => {
    drawingManager.current = new TerraDrawManager({
      drawingMode: 'rectangle',
      drawingControl: true,
      drawingControlOptions: { drawingModes: ['rectangle', 'polygon'] },
      polygonOptions: {
        fillColor: '#00B3A1',
        fillOpacity: 0.2,
        strokeColor: '#003832',
        strokeOpacity: 1,
        strokeWeight: 0.5,
      },
      rectangleOptions: {
        fillColor: '#00B3A1',
        fillOpacity: 0.2,
        strokeColor: '#003832',
        strokeOpacity: 1,
        strokeWeight: 0.5,
      },
    });
    drawingManager.current.setMap(mapRef.current);
    drawingManager.current.addListener('overlaycomplete', handleDrawing);
    drawingManager.current.addListener('modechange', (mode) => {
      if (mode) drawShapeRef.current = mode;
    });
  }, [drawingManager]);

  /**
   * @function generateClassificationLayer
   * @description Generates and displays the classified land-cover raster for the active year.
   * 1. Builds a training FeatureCollection from the drawn polygons' bounds and LandCover labels
   *    (skipped for the "mapbiomas" path, which needs no training data).
   * 2. Classifies via MapBiomas (clipped to the AOI) or via a random forest trained on the
   *    polygons and the image's UI/NDVI/MNDWI bands, then renders a thumbnail overlay.
   * 3. Hides the training polygons, stores the classification/overlay in `layers`, switches to
   *    the "view result" map mode, and marks the year as finished.
   * @param {Object} image - The active year's image layer entry.
   * @param {Array<Object>} polygsData - Drawn training polygons for the active year.
   * @param {String} [type=""] - Pass "mapbiomas" to classify via MapBiomas instead of training a classifier.
   * @see {@link getMapBiomasClassificationYear}
   * @see {@link trainAndClassify}
   * **Called by:
   * @see {LandCover} - via the "Generate raster" button's onClick, and handleMapBiomasClick.
   * @memberof module:Euscoast
   * @returns {void}
   */
  const generateClassificationLayer = (image, polygsData, type = '') => {
    let imagem = image.imageObject;
    let bands = ['UI', 'NDVI', 'MNDWI'];
    var polygs = polygsData.filter((p) => p !== null);
    let geojson;
    if (type === '') {
      const features = polygs.map((m) => ({
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [overlayToRingCoordinates(m.overlay)],
        },
        properties: {
          LandCover: m.overlay.LandCover || m.LandCover,
        },
      }));
      geojson = {
        type: 'FeatureCollection',
        features: features,
      };
    } else {
      geojson = {
        type: 'FeatureCollection',
        features: [],
      };
    }
    try {
      let classificationAreas = ee.FeatureCollection(geojson);
      let classified, classifiedMap;
      if (type === 'mapbiomas') {
        classified = getMapBiomasClassificationYear(coordinates, mapBiomasYear(tabKey));
        classifiedMap = classified.sldStyle(mapbiomas_lulc_class_color).getThumbURL({
          format: 'png',
          dimensions: 800,
          tileScale: 512,
        });
      } else {
        classified = trainAndClassify(imagem, classificationAreas, bands, AOI);
        let vis = {
          palette: pointerColors,
          min: 0,
          max: pointerColors.length - 1,
        };
        classifiedMap = classified.visualize(vis).getThumbURL({
          format: 'png',
          dimensions: 800,
          tileScale: 512,
        });
      }
      var overlay = new window.google.maps.GroundOverlay(classifiedMap, bounds.current);
      overlay.setMap(mapRef.current);
      setLayers((previous) => ({
        ...previous,
        [tabKey]: {
          ...previous[tabKey],
          type: type,
          classification: overlay,
          classified: classified,
        },
      }));

      if (polygs?.length > 0)
        polygs
          .filter((p) => p !== null)
          .forEach((point) => {
            point.overlay.setVisible(false);
            point.overlay.zIndex = 0;
          });
      layers[tabKey].classification = null;
      setMapMode(2);
      let yearsMap = years.map((info) => parseInt(info[0]));
      let tempFinish = [...finish];
      tempFinish[yearsMap.indexOf(parseInt(tabKey))] = true;
      setFinish(tempFinish);
    } catch (error) {
      console.error('[EUSCoast] the classification raster could not be generated', error);
      notifyError(error, { fallback: t('forms.euscoast.5.generateFailed') });
    }
  };

  /**
   * @function requestMedianImages
   * @description Groups the acquired dates by year, requests the median composite image for each
   * period, and activates the first period's tab. The periods come back keyed as the year, with a
   * `-1`/`-2` suffix for the years picked more than once, and alongside a label for each: the year
   * itself when it is alone, the period's `DD/MM/YY - DD/MM/YY` date range when it is not, so two
   * periods of one year are told apart by when they were acquired rather than by an index.
   *
   * In CSqueeze this ran from the "Confirm elevation" dialog button. EUSCoast has no elevation
   * step, so it runs once on mount instead (see the effect below) - without it step 5 would render
   * empty and never ask Earth Engine for the composites.
   * **Called by:
   * @see {LandCover} - via the acquisition effect, once per mount.
   * @see {buildPeriodIndex}
   * @memberof module:Euscoast
   * @returns {void}
   */
  const requestMedianImages = () => {
    const { entries: imageYears, labels } = buildPeriodIndex(imageLists);
    setYears(imageYears);
    dispatch(EUSCoast.setPeriodLabels(labels));
    dispatch(EUSCoast.acquireImagesMedian(imageYears));
    changeTab(tabKey);
  };

  useEffect(() => {
    if (medianImagesRequested.current) return;
    if (!imageLists || imageLists.length === 0) return;
    medianImagesRequested.current = true;
    requestMedianImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageLists]);

  /**
   * @function isPolygonInside
   * @description Checks whether every vertex of a polygon falls within another polygon.
   * @param {google.maps.Polygon} polygonInner - Polygon whose vertices are tested.
   * @param {google.maps.Polygon} polygonOuter - Containing polygon.
   * **Called by:
   * @see {isRectangleInside}
   * @memberof module:Euscoast
   * @returns {boolean} True if every vertex of `polygonInner` is inside `polygonOuter`.
   */
  const isPolygonInside = (polygonInner, polygonOuter) => {
    for (let i = 0; i < polygonInner.getPath().getLength(); i++) {
      if (!window.google.maps.geometry.poly.containsLocation(polygonInner.getPath().getAt(i), polygonOuter)) {
        return false;
      }
    }
    return true;
  };

  /**
   * @function isRectangleInside
   * @description Checks whether a rectangle's bounds fall entirely within a polygon.
   * @param {google.maps.Rectangle} rectangle - Rectangle to test.
   * @param {google.maps.Polygon} polygon - Containing polygon.
   * @see {@link isPolygonInside}
   * **Called by:
   * @see {handleDrawing}
   * @memberof module:Euscoast
   * @returns {boolean} True if the rectangle is entirely inside the polygon.
   */
  const isRectangleInside = (rectangle, polygon) => {
    const bounds = rectangle.getBounds();
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const polygonInner = new window.google.maps.Polygon({
      paths: [
        { lat: ne.lat(), lng: ne.lng() },
        { lat: ne.lat(), lng: sw.lng() },
        { lat: sw.lat(), lng: sw.lng() },
        { lat: sw.lat(), lng: ne.lng() },
      ],
    });
    return isPolygonInside(polygonInner, polygon);
  };

  /**
   * @function handleFinish
   * @description Commits both years' classified layers and marked training points as the
   * study's data, advancing to the next step.
   * **Called by:
   * @see {LandCover} - via StepperButtons' onNext.
   * @memberof module:Euscoast
   * @returns {void}
   */
  const handleFinish = () => {
    dispatch(EUSCoast.setStudyData([layers, markedPoints]));
  };

  /**
   * @function handleUndo
   * @description Removes every marked training polygon from the active year's map view and clears them.
   * **Called by:
   * @see {LandCover} - via the "Undo" button's onClick.
   * @memberof module:Euscoast
   * @returns {void}
   */
  const handleUndo = () => {
    markedPoints.current[tempTab.current]
      .filter((m) => m !== null)
      .forEach((polyg) => {
        polyg.overlay.setVisible(false);
        polyg.overlay.setMap(null);
      });
    markedPoints.current[tempTab.current] = [];
    setMarkedPointsCount(Date.now());
  };

  /**
   * @function changeTab
   * @description Switches the active year: hides the other year's overlays/training polygons and
   * shows the selected year's (classification, false-color, or true-color overlay, whichever
   * applies) along with its training polygons.
   * @param {String|Number} tab - Date key of the year to activate.
   * **Called by:
   * @see {LandCover} - via the tabKey/falseColor effect (triggered by the year buttons' setTabKey).
   * @see {requestMedianImages}
   * @memberof module:Euscoast
   * @returns {void}
   */
  const changeTab = (tab) => {
    if (Object.values(layers).length < 2) return;
    const layersValues = Object.values(layers);
    const old = layersValues.filter((l) => l.date !== tab);
    const atual = layersValues.filter((l) => l.date === tab);
    old.forEach((layer) => {
      layer.overlay.setMap(null);
      layer.overlayFalseColor.setMap(null);
      if (layer.classification) layer.classification.setMap(null);
      if (markedPoints.current[layer.date] && markedPoints.current[layer.date].length > 0)
        markedPoints.current[layer.date].forEach((polyg) => {
          if (polyg) polyg.overlay.setVisible(false);
        });
    });
    atual.forEach((layer) => {
      if (layer.classification) {
        layer.classification.setMap(mapRef.current);
      } else if (falseColor) {
        layer.overlayFalseColor.setMap(mapRef.current);
      } else {
        layer.overlay.setMap(mapRef.current);
      }
      if (
        markedPoints.current[layer.date] &&
        markedPoints.current[layer.date].length > 0 &&
        !layers[layer.date].classification
      )
        markedPoints.current[layer.date].forEach((polyg) => {
          if (polyg) polyg.overlay.setVisible(true);
        });
    });
  };

  /**
   * @function handleDrawing
   * @description Registers a drawn training shape (rectangle or polygon) as a land-cover marker,
   * if it falls entirely within the AOI: styles it with the selected land-cover's color, tags it
   * with the label, and (in "remove" mode) wires a click listener to delete it. Discards the
   * shape if it falls outside the AOI. After a shape drawn directly on the map (not one
   * restored from a CSV import) is registered, the drawing tool is switched off, so the next
   * click doesn't immediately start another shape — the user must pick a land-cover type again
   * (see `handleSelect`) to resume drawing.
   * @param {Object} polyg - Drawn shape: `overlay` (google.maps.Rectangle|google.maps.Polygon)
   * and `type` ('rectangle'|'polygon'|'imported').
   * @param {Number} [type=null] - Land-cover value to use instead of the currently selected one
   * (used when importing polygons from a file).
   * @see {@link isRectangleInside}
   * @see {@link isPolygonInside}
   * **Called by:
   * @see {LandCover} - via the DrawingManager's 'overlaycomplete' listener.
   * @see {readFileTrainingData} - for each imported polygon.
   * @memberof module:Euscoast
   * @returns {void}
   */
  const handleDrawing = (polyg, type = null) => {
    const insideAOI =
      typeof polyg.overlay.getBounds === 'function'
        ? isRectangleInside(polyg.overlay, AOI.current)
        : isPolygonInside(polyg.overlay, AOI.current);
    if (insideAOI) {
      polyg.overlay.setOptions({
        fillColor: landCovers[type ?? selectedLandCoverRef.current].color,
        fillOpacity: 0.5,
        strokeColor: landCovers[type ?? selectedLandCoverRef.current].color,
        strokeOpacity: 1,
        strokeWeight: 1,
        clicable: true,
        zIndex: 90,
      });
      polyg.overlay.set('from', 'user');
      polyg.overlay.set('LandCover', landCovers[type ?? selectedLandCoverRef.current].value);
      polyg.LandCover = landCovers[type ?? selectedLandCoverRef.current].value;
      if (markedPoints.current[tempTab.current] === undefined) markedPoints.current[tempTab.current] = [];
      markedPoints.current[tempTab.current].push(polyg);
      let positionArray = markedPoints.current[tempTab.current].length - 1;
      window.google.maps.event.addListener(polyg.overlay, 'click', () => {
        if (mapModeRef.current === 5) {
          polyg.overlay.setVisible(false);
          polyg.overlay.setMap(null);
          markedPoints.current[tempTab.current][positionArray] = null;
          setMarkedPointsCount(Math.random());
        }
      });
    } else {
      polyg.overlay.setMap(null);
    }
    if (polyg.type !== 'imported') drawingManager.current.setDrawingMode(null);
    setMarkedPointsCount(Date.now());
  };

  /**
   * @function restoreSavedShapes
   * @description Redraws the training shapes previously saved for a given year (see the
   * `markedPointsCount` persistence effect below), so returning to this step after navigating
   * to a later one and back restores them instead of starting from a blank map.
   * @param {String|Number} year - Year key to restore shapes for.
   * @see {@link handleDrawing}
   * **Called by:
   * @see {LandCover} - via the `[images]` effect, once per year as its layer is set up.
   * @memberof module:Euscoast
   * @returns {void}
   */
  const restoreSavedShapes = (year) => {
    const saved = JSON.parse(window.sessionStorage.getItem(LANDCOVER_SHAPES_STORAGE_KEY) || '{}');
    const shapes = saved[year];
    if (!shapes || !shapes.length) return;
    const previousTab = tempTab.current;
    tempTab.current = year;
    shapes.forEach(({ landCover, ring }) => {
      const path = ring.map(([lng, lat]) => ({ lat, lng }));
      const overlay = new window.google.maps.Polygon({ map: mapRef.current, paths: path });
      handleDrawing({ type: 'imported', overlay }, landCover);
    });
    tempTab.current = previousTab;
  };

  /**
   * @description Persists every year's currently marked training shapes to sessionStorage
   * whenever they change, as `{ [year]: [{ landCover, ring }] }` — read back by
   * `restoreSavedShapes` on remount.
   * @see {@link restoreSavedShapes}
   * @see {@link overlayToRingCoordinates}
   * @memberof module:Euscoast
   */
  useEffect(() => {
    const serializable = {};
    Object.entries(markedPoints.current).forEach(([year, points]) => {
      serializable[year] = (points || [])
        .filter((p) => p !== null)
        .map((p) => ({
          landCover: p.overlay.LandCover ?? p.LandCover,
          ring: overlayToRingCoordinates(p.overlay),
        }));
    });
    window.sessionStorage.setItem(LANDCOVER_SHAPES_STORAGE_KEY, JSON.stringify(serializable));
  }, [markedPointsCount]);

  /**
   * @function handleSelect
   * @description Selects the active land-cover type (used for newly drawn training polygons),
   * switches the map mode to match, and reactivates the drawing tool (handleDrawing switches it
   * off after each finished shape). Called directly rather than left to the `[mapMode]` effect,
   * since re-picking the same type value leaves `mapMode` unchanged and wouldn't re-run it.
   * @param {Number} value - Land-cover value to select.
   * **Called by:
   * @see {LandCover} - via each land-cover radio's onClick.
   * @memberof module:Euscoast
   * @returns {void}
   */
  const handleSelect = (value) => {
    selectedLandCoverRef.current = value;
    setSelectedLandCover(value);
    setMapMode(value);
    drawingManager.current.setDrawingMode(drawShapeRef.current);
  };

  /**
   * @function handleFullScreen
   * @description Toggles fullscreen mode for the map/classification container, resizing it to
   * fit the viewport.
   * **Called by:
   * @see {LandCover} - via the fullscreen button's onClick.
   * @memberof module:Euscoast
   * @returns {Promise<void>}
   */
  const handleFullScreen = async () => {
    if (document.fullscreenElement) {
      landCoverRef.current.style = 'width: 95vw; height: 100vh; top: 0; left: 0; position: relative; overflow: hidden;';
      await document.exitFullscreen();
    } else {
      landCoverRef.current.style =
        'width: 100vw; height: 100vh; top: 0; left: 0; position: fixed; z-index: 999999; overflow: hidden;';
      await document.body.requestFullscreen();
    }
  };

  /**
   * @function listenerCtrlKey
   * @description Toggles the drawing manager's drawing mode based on whether Ctrl/Cmd is held,
   * so the user can hold the key to pan the map instead of drawing a new rectangle.
   * @param {KeyboardEvent} e - Keydown/keyup event.
   * **Called by:
   * @see {LandCover} - via document's keydown/keyup listeners, registered while not in "remove" mode.
   * @memberof module:Euscoast
   * @returns {void}
   */
  const listenerCtrlKey = useCallback((e) => {
    drawingManager.current.setDrawingMode(e.ctrlKey || e.cmdKey ? null : drawShapeRef.current);
  });

  useEffect(() => {
    if (mapMode !== 5) {
      document.addEventListener('keydown', listenerCtrlKey);
      document.addEventListener('keyup', listenerCtrlKey);
      drawingManager.current.setDrawingMode(drawShapeRef.current);
    } else {
      document.removeEventListener('keydown', listenerCtrlKey);
      document.removeEventListener('keyup', listenerCtrlKey);
      drawingManager.current.setDrawingMode(null);
    }
    return () => {
      document.removeEventListener('keydown', listenerCtrlKey);
      document.removeEventListener('keyup', listenerCtrlKey);
    };
  }, [mapMode]);

  /**
   * @function handleGenerateClassificationDiscard
   * @description Discards the active year's generated classification: removes its overlay,
   * clears it from `layers`, switches back to drawing mode, and re-shows the training polygons.
   * @param {Array<Object>} polygs - The active year's training polygons to restore.
   * **Called by:
   * @see {LandCover} - via the "Discard classification" button's onClick.
   * @memberof module:Euscoast
   * @returns {void}
   */
  const handleGenerateClassificationDiscard = (polygs) => {
    layers[tabKey].classification.setMap(null);
    setLayers((previous) => ({
      ...previous,
      [tabKey]: { ...previous[tabKey], classification: null },
    }));
    setMapMode(1);
    polygs
      .filter((p) => p !== null)
      .forEach((point) => {
        point.overlay.zIndex = 90;
        point.overlay.setVisible(true);
      });
  };

  /**
   * @function readFileTrainingData
   * @description Reads a semicolon-delimited CSV of training shapes and registers each as a
   * drawn training marker. Supports two row formats:
   *  - `landCoverCode;lat,lng|lat,lng|...` (current format, any shape — rectangle or polygon).
   *  - `landCoverCode;south;west;north;east` (legacy rectangle-only format, for CSVs exported
   *    before polygon support was added).
   * @see {@link handleDrawing}
   * **Called by:
   * @see {LandCover} - via the training-data file input's onChange.
   * @memberof module:Euscoast
   * @returns {boolean|void} false if no file was selected; otherwise void.
   */
  const readFileTrainingData = () => {
    let fileField = document.getElementById('file_import_training_data');
    if (fileField.files.length === 0) {
      notifyError(t('forms.core.fileErrors.selectFileFirst'));
      return false;
    }
    const file = fileField.files[0];
    const reader = new FileReader();
    reader.addEventListener(
      'load',
      () => {
        const fileContent = reader.result;
        let lines = fileContent.split('\n');
        lines = lines.splice(1, lines.length);
        lines.forEach((line) => {
          let columns = line.split(';');
          if (columns.length === 5 && columns.every((column) => column !== undefined || column !== null)) {
            let landCoverCode = parseInt(columns[0]);
            let overlay = new window.google.maps.Rectangle({
              map: mapRef.current,
              bounds: {
                south: parseFloat(columns[1]),
                west: parseFloat(columns[2]),
                north: parseFloat(columns[3]),
                east: parseFloat(columns[4]),
              },
            });
            handleDrawing({ type: 'imported', overlay }, landCoverCode);
          } else if (columns.length === 2) {
            const landCoverCode = parseInt(columns[0]);
            const path = columns[1]
              .split('|')
              .map((pair) => pair.split(',').map(Number))
              .filter(([lat, lng]) => !isNaN(lat) && !isNaN(lng))
              .map(([lat, lng]) => ({ lat, lng }));
            if (path.length < 3) return;
            const overlay = new window.google.maps.Polygon({ map: mapRef.current, paths: path });
            handleDrawing({ type: 'imported', overlay }, landCoverCode);
          }
        });
        setPolygonsFileProcessedDialogOpen(true);
        fileField.value = null;
      },
      false,
    );
    if (file) reader.readAsText(file);
  };

  /**
   * @function exportToCsv
   * @description Builds a semicolon-delimited CSV from the given rows and triggers its download.
   * @param {String} filename - Download filename (without extension).
   * @param {Array<Array>} rows - Rows to serialize.
   * **Called by:
   * @see {exportRectanglesToCsv}
   * @memberof module:Euscoast
   * @returns {void}
   */
  const exportToCsv = (filename, rows) => {
    const csvContent = 'data:text/csv;charset=utf-8,type;points\n' + rows.map((row) => row.join(';')).join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /**
   * @function exportShapesToCsv
   * @description Converts training shapes (rectangles or polygons) to CSV rows — land-cover type
   * plus a `lat,lng|lat,lng|...` vertex list — and exports them.
   * @param {Array<Object>} shapes - Training shapes to export.
   * @see {@link overlayToRingCoordinates}
   * @see {@link exportToCsv}
   * **Called by:
   * @see {exportPoints}
   * @memberof module:Euscoast
   * @returns {void}
   */
  const exportShapesToCsv = (shapes) => {
    const rows = [];
    shapes.forEach((shape) => {
      const type = shape.overlay.LandCover;
      const points = overlayToRingCoordinates(shape.overlay)
        .map(([lng, lat]) => `${lat},${lng}`)
        .join('|');
      rows.push([type, points]);
    });
    exportToCsv(`cassie_euscoast_${tempTab.current}_landcovers_${new Date().getTime()}`, rows);
  };

  /**
   * @function exportPoints
   * @description Exports the active year's marked training shapes as a CSV file.
   * @see {@link exportShapesToCsv}
   * **Called by:
   * @see {LandCover} - via the "Export points" button's onClick.
   * @memberof module:Euscoast
   * @returns {void}
   */
  const exportPoints = () => {
    exportShapesToCsv(markedPoints.current[tabKey].filter((m) => m !== null));
  };

  /**
   * @function handleFalseColor
   * @description Toggles displaying the false-color composite instead of true-color.
   * @param {Event} event - Change event from the false-color checkbox.
   * **Called by:
   * @see {LandCover} - via the false-color checkbox's onChange.
   * @memberof module:Euscoast
   * @returns {void}
   */
  const handleFalseColor = (event) => {
    setFalseColor(event.target.checked);
  };

  document.addEventListener('fullscreenchange', () => {
    if (!document.fullscreenElement)
      landCoverRef.current.style = landCoverRef.current.style =
        'width: 95vw; height: 100vh; top: 0; left: 0; position: relative; overflow: hidden;';
  });

  /**
   * @function isClassified
   * @description Whether a year already has its raster.
   * The test is that the classification can be put on the map, which is the only thing the rest of
   * this step ever does with it. It used to count the object's own properties instead - and a
   * Google Maps overlay carries whatever internals that version of the API happens to expose, none
   * of which say anything about whether the year was classified. A layer that arrived already
   * classified, which is how an imported session comes back, could therefore be rejected outright,
   * leaving the step refusing to advance over a raster that was sitting right there.
   * @param {Object} [layer] - A year's layer.
   * @memberof module:LandCover
   * @returns {Boolean} True when the year has a usable classification.
   */
  const isClassified = (layer) => typeof layer?.classification?.setMap === 'function';

  const nextDisabled = !Object.values(layers).every(isClassified);

  /**
   * @description The classifier needs at least 2 distinct land-cover classes to discriminate
   * between, but not necessarily any *specific* one — mangrove, for instance, isn't always
   * present in the ROI. `canGenerateRaster` checks the active year has that minimum, without
   * requiring every one of the 4 classes to be drawn.
   * @memberof module:Euscoast
   */
  const activeYearShapes = (markedPoints.current[tabKey] || []).filter((p) => p !== null);
  const canGenerateRaster = new Set(activeYearShapes.map((p) => p.LandCover)).size >= 2;

  const steps = [
    {
      selector: '#selectType',
      content: t('tour.euscoast.5.selectType'),
    },
    {
      selector: '#falseColorButton',
      content: t('tour.euscoast.5.falseColorButton'),
    },
    {
      selector: '#availableDataset',
      content: t('tour.euscoast.5.availableDataset'),
    },
    {
      selector: '#importPolygons',
      content: t('tour.euscoast.5.importPolygons'),
    },
    {
      selector: '#generateRaster',
      content: t('tour.euscoast.5.generateRaster'),
    },
    {
      selector: '#yearsButtons',
      content: t('tour.euscoast.5.yearsButtons'),
    },
  ];

  /**
   * @function handleMapBiomasClick
   * @description Queues the MapBiomas classification for the active year (behind a confirmation
   * dialog) and opens that dialog.
   * @see {@link generateClassificationLayer}
   * **Called by:
   * @see {LandCover} - via the "Generate MapBiomas" button's onClick.
   * @memberof module:Euscoast
   * @returns {void}
   */
  const handleMapBiomasClick = () => {
    setMapBiomasConfirmAction(() => () => {
      generateClassificationLayer(layers[tabKey], markedPoints.current[tabKey], 'mapbiomas');
      setMapBiomasDialogOpen(false);
    });
    setMapBiomasDialogOpen(true);
  };

  /**
   * @function handleDialogCancel
   * @description Closes whichever of the MapBiomas confirmation or polygons-imported dialogs is open.
   * **Called by:
   * @see {LandCover} - via both dialogs' onClose/"Cancel" handlers.
   * @memberof module:Euscoast
   * @returns {void}
   */
  const handleDialogCancel = () => {
    if (mapBiomasDialogOpen) setMapBiomasDialogOpen(false);
    if (polygonsFileProcessedDialogOpen) setPolygonsFileProcessedDialogOpen(false);
  };

  return (
    <React.Fragment>
      <Dialog
        open={mapBiomasDialogOpen}
        onClose={handleDialogCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          style: { padding: 20, borderRadius: 12, textAlign: 'center' },
        }}>
        <DialogTitle id="alert-dialog-title" style={{ textAlign: 'center' }}>
          {t('forms.euscoast.5.mapBiomasAlertTitle')}
        </DialogTitle>
        <DialogContent style={{ fontSize: '16px' }}>
          <Typography variant="body1">{t('forms.euscoast.5.mapBiomasAlertText')}</Typography>
        </DialogContent>
        <DialogActions style={{ justifyContent: 'center' }}>
          <Button onClick={handleDialogCancel} color="default">
            {t('forms.euscoast.5.alertDialogCancel')}
          </Button>
          <Button onClick={() => mapBiomasConfirmAction()} color="primary" autoFocus>
            {t('forms.euscoast.5.alertDialogConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={polygonsFileProcessedDialogOpen}
        onClose={handleDialogCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{ style: { padding: 20, borderRadius: 12 } }}>
        <DialogContent style={{ textAlign: 'center' }}>
          <h2 style={{ marginTop: 0 }}>{t('forms.euscoast.2.infoTitle')}</h2>
          <p style={{ fontSize: 18 }}>{t('forms.euscoast.5.polygonsFileProcessed')}</p>
        </DialogContent>
        <DialogActions style={{ justifyContent: 'center' }}>
          <Button onClick={handleDialogCancel} color="primary" variant="contained">
            {t('forms.euscoast.5.alertDialogClose')}
          </Button>
        </DialogActions>
      </Dialog>
      <Box className={classes.wrapper}>
        <HelpButton
          onClickFunction={() => {
            setIsTourOpen(true);
          }}
        />
        <div
          id="landcover"
          ref={landCoverRef}
          style={{
            width: '95vw',
            height: '100vh',
            top: 0,
            rigth: 0,
            position: 'relative',
            overflow: 'hidden',
          }}>
          <Box
            style={{
              width: 'fit-content',
              textAlign: 'center',
              height: 'fit-content',
              padding: 10,
              display: 'block',
              position: 'absolute',
              zIndex: 2,
              marginTop: 85,
              left: '50%',
              transform: 'translateX(-50%)',
            }}>
            <Typography
              style={{
                boxShadow: `0px 2px 5px 2px #00000`,
                border: `2px solid ${landCovers[selectedLandCover].color}`,
                backgroundColor: 'white',
                width: 300,
                margin: '0 auto',
                borderRadius: 5,
                padding: '10px 20px',
              }}>
              <b style={{ textTransform: 'uppercase' }}>
                {mapMode !== 5 ? t('forms.euscoast.5.drawingMode') : t('forms.euscoast.5.drawingModeRemove')}
              </b>
              <br />
              {mapMode !== 5 ? (
                <>
                  {t('forms.euscoast.5.drawingModeText')}
                  <b style={{ color: landCovers[selectedLandCover].color }}>{landCovers[selectedLandCover].class}</b>.
                </>
              ) : (
                t('forms.euscoast.5.drawingModeTextRemove')
              )}
            </Typography>
          </Box>
          <Grid
            id="landcovermarker"
            className={classes.mapOverlay}
            style={{
              display: 'grid',
              marginLeft: '10px',
              marginTop: LANDCOVER_COLUMN_TOP_INSET,
              width: '350px',
              gap: OVERLAY_PANEL_GAP,
              overflowY: 'auto',
              overflowX: 'hidden',
              minHeight: 0,
              maxHeight: `calc(100vh - ${LANDCOVER_COLUMN_BOTTOM_RESERVE}px)`,
              gridAutoRows: 'max-content',
              alignContent: 'start',
              paddingBottom: OVERLAY_COLUMN_BOTTOM_SPACE,
            }}>
            {layers[tabKey] !== undefined && (
              <React.Fragment>
                <Card style={{ padding: 10 }} id="yearsButtons">
                  <CardContent style={{ padding: 0 }}></CardContent>
                  <CardActions>
                    <Box
                      width="100%"
                      display="grid"
                      gridTemplateColumns="auto auto"
                      style={{
                        gap: 5,
                        '&:lastChild': { gridColumn: '1 / 3' },
                      }}
                      fontSize="small"
                      fontWeight={300}>
                      {years.map(([year, _]) => (
                        <Button
                          key={year}
                          variant={year === tabKey ? 'contained' : 'outlined'}
                          color={year === tabKey ? 'primary' : 'default'}
                          onClick={() => {
                            if (years.map((y) => y[0]).indexOf(year) > -1) setTabKey(year);
                          }}>
                          {layers[year] !== undefined && layers[year].classification && <DoneIcon />}
                          {periodLabels[year] ?? year}
                        </Button>
                      ))}
                    </Box>
                  </CardActions>
                </Card>
                {!layers[tabKey].classification && (
                  <React.Fragment>
                    <Card style={{ padding: 10 }} id="selectType">
                      <CardContent style={{ padding: 0 }}>
                        <Typography variant="subtitle2" align="center">
                          {t('forms.euscoast.5.defineLandCovers')}
                        </Typography>
                      </CardContent>
                      <CardActions
                        style={{
                          display: 'flex',
                          flexDirection: 'rows',
                          gap: '10px',
                        }}>
                        <Grid
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'auto 1fr',
                            gridTemplateRows: 'auto auto',
                            alignItems: 'center',
                          }}>
                          {landCoversDrawOrder.map((land, i) => (
                            <React.Fragment key={i}>
                              <Radio
                                checked={selectedLandCover === land.value}
                                onChange={(e) => {
                                  handleSelect(land.value);
                                }}
                                value={land.value}
                                name="radio-button-demo"
                                inputProps={{ 'aria-label': land.label }}
                              />
                              <FormLabel
                                style={{
                                  color: 'white',
                                  backgroundColor: land.color,
                                  padding: '5px 10px',
                                  borderRadius: '10px',
                                }}
                                onClick={(e) => {
                                  handleSelect(land.value);
                                }}>
                                {land.label}
                              </FormLabel>
                            </React.Fragment>
                          ))}
                          <Typography
                            variant="caption"
                            align="center"
                            style={{
                              gridColumn: '1 / 3',
                              marginTop: 10,
                            }}>
                            {t('forms.euscoast.5.shortcut')}
                          </Typography>
                        </Grid>
                      </CardActions>
                    </Card>
                    {hasMapBiomasFor(tabKey) && (
                      <Card style={{ padding: 10 }} id="availableDataset">
                        <CardContent style={{ padding: 0 }}>
                          <Typography variant="subtitle2" align="center">
                            {t('forms.euscoast.5.availableDataset')}
                          </Typography>
                        </CardContent>
                        <CardActions
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr auto',
                            gap: '10px',
                          }}>
                          <Typography>
                            {t('forms.euscoast.5.availableDatasetText')} <b>MapBiomas</b>.
                          </Typography>
                          <Button
                            variant="contained"
                            style={{ height: 35, padding: 'inherit 50px' }}
                            onClick={handleMapBiomasClick}>
                            {t('forms.euscoast.5.apply')}
                          </Button>
                        </CardActions>
                      </Card>
                    )}
                    <Card style={{ padding: 10 }} id="importPolygons">
                      <CardContent style={{ padding: 0 }}>
                        <Typography variant="subtitle2" align="center">
                          {t('forms.euscoast.5.importPolygons')}
                        </Typography>
                        <Box align="center" fontSize={10}>
                          {t('forms.euscoast.5.formatImport')}
                        </Box>
                      </CardContent>
                      <CardActions>
                        <Box
                          justifyContent="center"
                          alignItems="center"
                          width="100%"
                          display="flex"
                          flexDirection="column"
                          style={{ gap: 20 }}>
                          <Box
                            style={{
                              width: '100%',
                              backgroundColor: 'rgba(0, 0, 0, 0.09)',
                              borderTopLeftRadius: '4px',
                              borderTopRightRadius: '4px',
                              padding: '15px 20px',
                              borderBottom: '1px solid',
                            }}>
                            <input
                              id="file_import_training_data"
                              accept=".csv"
                              type="file"
                              onChange={readFileTrainingData}
                            />
                          </Box>
                        </Box>
                      </CardActions>
                      <Box align="center" fontSize="smaller" fontStyle="italic">
                        ({t('forms.euscoast.5.spanImport')})
                      </Box>
                    </Card>
                    <Card style={{ padding: 10 }} id="generateRaster">
                      <CardActions>
                        <Box
                          justifyContent="center"
                          alignItems="center"
                          width="100%"
                          display="flex"
                          flexDirection="column"
                          style={{ gap: 20 }}>
                          <Box
                            display="grid"
                            width="100%"
                            justifyContent="center"
                            gridTemplateColumns="1fr 1fr"
                            style={{ gap: 20 }}>
                            <Button
                              color="secondary"
                              variant="contained"
                              style={{ height: 35, padding: 'inherit 20px' }}
                              disabled={markedPointsCount && (markedPoints.current[tabKey] || []).length === 0}
                              onClick={exportPoints}>
                              {t('forms.euscoast.5.export')}
                            </Button>
                            <Button
                              onClick={handleUndo}
                              disabled={(markedPoints.current[tabKey] || []).length === 0}
                              color="secondary"
                              variant="outlined">
                              {t('forms.euscoast.5.undo')}
                            </Button>
                            {canGenerateRaster ? (
                              <Button
                                color="primary"
                                variant="contained"
                                onClick={() =>
                                  generateClassificationLayer(layers[tabKey], markedPoints.current[tabKey])
                                }
                                style={{ gridColumn: '1 / 3' }}>
                                {t('forms.euscoast.5.generate')}
                              </Button>
                            ) : (
                              <Box display="block" fontSize={12} style={{ gridColumn: '1 / 3' }} align="center">
                                {t('forms.euscoast.5.toContinue')}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </CardActions>
                    </Card>
                  </React.Fragment>
                )}
                {layers[tabKey].classification && (
                  <React.Fragment>
                    <Card style={{ padding: 10 }}>
                      <CardContent style={{ padding: 0 }}>
                        <Typography variant="subtitle2" align="center">
                          {t('forms.euscoast.5.labels')}
                        </Typography>
                      </CardContent>
                      <CardActions>
                        <Box
                          width="100%"
                          display="grid"
                          gridTemplateColumns="auto auto"
                          style={{
                            gap: 5,
                            '&:lastChild': { gridColumn: '1 / 3' },
                          }}
                          fontSize="small"
                          fontWeight={300}>
                          {landCovers
                            .filter((land) => land.value !== 5)
                            .map((land, i) => (
                              <Box
                                key={i}
                                display="flex"
                                alignItems="center"
                                style={{
                                  position: 'relative',
                                  gridColumn: land.value === 6 ? '1 / 3' : 'unset',
                                }}>
                                <FiberManualRecordIcon style={{ color: land.color }} />
                                {land.label}
                                <Box fontSize={9} marginLeft={0.5}>
                                  (cod {land.value})
                                </Box>
                              </Box>
                            ))}
                        </Box>
                      </CardActions>
                    </Card>
                    <Card style={{ padding: 10 }}>
                      <CardContent style={{ padding: 0 }}>
                        <Typography variant="subtitle2" align="center">
                          {t('forms.euscoast.5.confirmRaster')} {periodLabels[tabKey] ?? tabKey}
                        </Typography>
                      </CardContent>
                      <CardActions display="flex" style={{ flexDirection: 'column' }}>
                        <Box display="block" align="center" fontSize={12}>
                          {t('forms.euscoast.5.confirmRasterText')}
                        </Box>
                        <Button
                          style={{ marginTop: 10 }}
                          color="secondary"
                          variant="outlined"
                          onClick={() => handleGenerateClassificationDiscard(markedPoints.current[tabKey])}>
                          {t('forms.euscoast.5.editPolygons')}
                        </Button>
                      </CardActions>
                    </Card>{' '}
                  </React.Fragment>
                )}
              </React.Fragment>
            )}
          </Grid>
          <div id="google-maps" ref={(r) => (mapDiv.current = r)} style={{ height: '100%' }}></div>
          <Box
            id="falseColorButton"
            style={{
              width: 'fit-content',
              height: 40,
              position: 'absolute',
              top: 12,
              right: 60,
              backgroundColor: 'white',
              boxShadow: 'rgb(0 0 0 / 30%) 0px 1px 4px -1px',
              borderRadius: 2,
              padding: '0 10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <Typography>{t('forms.euscoast.5.falseColor')}</Typography>
            <Checkbox
              checked={falseColor}
              onClick={handleFalseColor}
              inputProps={{ 'aria-label': 'primary checkbox' }}
            />
          </Box>
          <IconButton
            onClick={handleFullScreen}
            style={{
              width: 40,
              height: 40,
              position: 'absolute',
              top: 12,
              right: 10,
              backgroundColor: 'white',
              boxShadow: 'rgb(0 0 0 / 30%) 0px 1px 4px -1px',
              borderRadius: 2,
            }}>
            <Fullscreen fontSize="large" />
          </IconButton>
        </div>
        <Typography variant="body2" style={{ marginTop: 10, marginBottom: 10, textAlign: 'center' }}>
          {t('forms.euscoast.5.nextStepAlert')}
        </Typography>
        <StepperButtons
          navigate={navigate}
          onBack={() => dispatch(EUSCoast.removeLastImageList())}
          onNext={handleFinish}
          nextTarget={FINALIZE}
          nextDisabled={nextDisabled}
        />
        <TourGuider steps={steps} isOpen={isTourOpen} setIsTourOpen={setIsTourOpen} />
      </Box>
    </React.Fragment>
  );
};

export default LandCover;
